import { Denominator } from './Denominator';
import { autoFormatAmount, getLargestDenominator, NumberFormatConfig } from './NumberFormatter';

export enum ValueType {
  ABSOLUTE,
  PERCENTAGE_CHANGE,
  ABSOLUTE_CHANGE,
}

export enum Kpi {
  salesVolume = 'salesVolume',
  salesVolumeShare = 'salesVolumeShare',
  revenue = 'revenue',
  revenueShare = 'revenueShare',
  revenueMix = 'revenueMix',
  averagePrice = 'averagePrice',
  priceElasticity = 'priceElasticity',
  marketRevenue = 'marketRevenue',
  marketVolume = 'marketVolume',
  yourRevenue = 'yourRevenue',
  yourVolume = 'yourVolume',
  marketPrice = 'marketPrice',
  yourPrice = 'yourPrice',
  yourRevenueShare = 'yourRevenueShare',
  yourVolumeShare = 'yourVolumeShare',
  yourRevenueMix = 'yourRevenueMix',
  yourVolumeMix = 'yourVolumeMix',
  retailerRevenue = 'retailerRevenue',
  retailerVolume = 'retailerVolume',
  retailerPrice = 'retailerPrice',
  marketShareRevenue = 'marketShareRevenue',
  marketShareVolume = 'marketShareVolume',
  retailerShareRevenue = 'retailerShareRevenue',
  retailerShareVolume = 'retailerShareVolume',
  retailerMixRevenue = 'retailerMixRevenue',
  retailerMixVolume = 'retailerMixVolume',
  marketRetailerRevenueShare = 'marketRetailerRevenueShare',
  marketRetailerVolumeShare = 'marketRetailerVolumeShare',
  marketExcludeRetailRevenue = 'marketExcludeRetailRevenue',
  marketExcludeRetailVolume = 'marketExcludeRetailVolume',
  marketExcludeRetailPrice = 'marketExcludeRetailPrice',
}

export const NOT_AVAILABLE = '-';

export const defaultCurrency = '??';

export interface Options {
  as?: ValueType;
  currencySymbol?: string;
  isDecimalPercent?: boolean;
}

export const formatKpi = (kpi: Kpi, value?: number | null, options: Options = {}): string => {
  if (value === null || value === undefined) {
    return NOT_AVAILABLE;
  }

  switch (kpi) {
    case Kpi.averagePrice:
    case Kpi.revenue:
    case Kpi.marketRevenue:
    case Kpi.marketPrice:
    case Kpi.yourPrice:
    case Kpi.yourRevenue:
    case Kpi.retailerRevenue:
    case Kpi.retailerPrice:
    case Kpi.marketRetailerRevenueShare:
    case Kpi.marketExcludeRetailRevenue:
    case Kpi.marketExcludeRetailPrice:
      return formatMoneyKpi(value, options);
    case Kpi.salesVolume:
    case Kpi.marketVolume:
    case Kpi.yourVolume:
    case Kpi.retailerVolume:
    case Kpi.marketRetailerVolumeShare:
    case Kpi.marketExcludeRetailVolume:
      return formatVolumeKpi(value, options);
    case Kpi.revenueMix:
    case Kpi.revenueShare:
    case Kpi.salesVolumeShare:
    case Kpi.yourVolumeShare:
    case Kpi.yourRevenueShare:
    case Kpi.yourVolumeMix:
    case Kpi.yourRevenueMix:
    case Kpi.marketShareRevenue:
    case Kpi.marketShareVolume:
    case Kpi.retailerShareRevenue:
    case Kpi.retailerShareVolume:
    case Kpi.retailerMixRevenue:
    case Kpi.retailerMixVolume:
      return formatPercentageKpi(value, { ...options, isDecimalPercent: false });
    case Kpi.priceElasticity:
      return formatDecimal(value, 1);
    default:
      return 'ERROR';
  }
};

export const formatPercentageKpi = (value: number, options: Options): string => {
  const defaultOptions: Options = {
    as: ValueType.ABSOLUTE,
  };

  options = {
    ...defaultOptions,
    ...options,
  };

  if (options.as === ValueType.ABSOLUTE_CHANGE) {
    let percentageValue = value;
    if (options.isDecimalPercent) {
      percentageValue = value * 100;
    }
    const formatted = formatDecimal(percentageValue, 1);
    return `${getChangedPrefix(value)}${formatted}pp`;
  }

  if (options.as === ValueType.PERCENTAGE_CHANGE) {
    const formatted = formatPercentage(value, 1, options.isDecimalPercent);
    return `${getChangedPrefix(value)}${formatted}`.replace('+', '');
  }

  return formatPercentage(value, 1, options.isDecimalPercent);
};

export const formatVolumeKpi = (value: number, options: Options): string => {
  const defaultOptions: Options = {
    as: ValueType.ABSOLUTE,
  };

  options = {
    ...defaultOptions,
    ...options,
  };

  if (options.as === ValueType.ABSOLUTE_CHANGE) {
    const formatted = formatVolume(value);
    return `${getChangedPrefix(value)}${formatted}`;
  }

  if (options.as === ValueType.PERCENTAGE_CHANGE) {
    const formatted = formatPercentage(value, 1);
    return `${getChangedPrefix(value)}${formatted}`;
  }

  return formatVolume(value);
};

export const formatMoneyKpi = (value: number, options: Options): string => {
  const defaultOptions: Options = {
    as: ValueType.ABSOLUTE,
  };

  options = {
    ...defaultOptions,
    ...options,
  };

  options.currencySymbol ??= defaultCurrency;

  if (options.as === ValueType.ABSOLUTE_CHANGE) {
    const formatted = formatCurrency(value, options.currencySymbol);
    return `${getChangedPrefix(value)}${formatted}`;
  }

  if (options.as === ValueType.PERCENTAGE_CHANGE) {
    const formatted = formatPercentage(value, 1, options.isDecimalPercent);
    return `${getChangedPrefix(value)}${formatted}`;
  }

  return formatCurrency(value, options.currencySymbol);
};

const getChangedPrefix = (value: number): string => {
  if (value >= 0) {
    return '+';
  } else {
    return '';
  }
};

type NumberType = number | null | undefined;

export const formatPercentage = (
  value: NumberType,
  decimals: number = 1,
  isDecimalPercent: boolean = false,
): string => {
  if (value === null || value === undefined) {
    return NOT_AVAILABLE;
  }

  const perValue = isDecimalPercent ? value * 100 : value;
  return autoFormatAmount(perValue, {
    decimals,
    percentage: true,
  });
};

export const formatCurrency = (value: NumberType, symbol: string): string => {
  if (value === null || value === undefined) {
    return NOT_AVAILABLE;
  }

  const config: NumberFormatConfig = {
    currency: symbol,
    decimals: 2,
  };

  config.denominator = getLargestDenominator(value);

  if (config.denominator !== Denominator.None) {
    config.decimals = 1;
  }

  return autoFormatAmount(value, config);
};

export const formatVolume = (value: NumberType): string => {
  if (value === null || value === undefined) {
    return NOT_AVAILABLE;
  }

  const config: NumberFormatConfig = {
    decimals: 0,
  };

  config.denominator = getLargestDenominator(value);

  if (config.denominator !== Denominator.None) {
    config.decimals = 1;
  }

  return autoFormatAmount(value, config);
};

export const formatDecimal = (value: NumberType, decimals: number = 1): string => {
  if (value === null || value === undefined) {
    return NOT_AVAILABLE;
  }

  return autoFormatAmount(value, { decimals, percentage: true }).replace('%', '');
};

/* Currently the y-axis and tooltip label are overlapping in Butterfly Chart
 * when Unicode cannot handle for specific currency vector (e.g. 5534د.إ )
 * <p>
 * \u202A is Unicode control character for LEFT-TO-RIGHT EMBEDDING
 * \u202C is POP DIRECTIONAL FORMATTING (PDF) character which
 * cancel the effect right after it applied to the value
 */
export const formatCurrencyPattern = (value: string): string => {
  return `\u202A${value}\u202C`;
};

export const formatPriceBandAsPerExchangeRate = (valueToTransform: string, currencyExchangeRate: number): string => {
  if (currencyExchangeRate <= 0) return valueToTransform;
  const numberExtractionRegex = valueToTransform.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  const numbers = numberExtractionRegex.match(/\d+(\.\d+)?/g);

  let transformed: string = numberExtractionRegex;
  if (numbers) {
    numbers.forEach((num) => {
      const multiplied = (parseFloat(num) * currencyExchangeRate).toFixed(1);
      transformed = transformed.replace(num, multiplied);
    });
  }
  return transformed;
};
